import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { X } from 'lucide-react';

import { closeModal } from '@/components/modals/modals.utils';
import { ModalBase } from '@/components/modals/modals-base';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';

import { ShareArchiveLinksTable } from '../ShareArchiveLinksTable';
import {
  createEstateFolderShare,
  type SharedFolderToken,
  useShareProjectFolder,
  useRevokeEstateFolderShareLink,
  useRevokeProjectFolderShareLink,
} from '@/core/api/services/archives';
import { useFolderShareLinks } from '../../hooks/useFolderShareLinks';

dayjs.extend(localizedFormat);

export interface ShareArchiveModalProps {
  id: string;
  folderId: string;
  resourceType?: 'estate' | 'project';
}

export const ShareArchiveModal: React.FC<ShareArchiveModalProps> = ({
  id,
  folderId,
  resourceType = 'estate',
}) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [links, setLinks] = useState<SharedFolderToken[]>([]);
  const [creating, setCreating] = useState(false);
  const [expiresIn, setExpiresIn] = useState<number>(730);
  const isEstate = resourceType === 'estate';
  const { mutateAsync: shareProjectFolder } = useShareProjectFolder(id);
  const { mutateAsync: revokeEstateLink } = useRevokeEstateFolderShareLink(id);
  const { mutateAsync: revokeProjectLink } = useRevokeProjectFolderShareLink(id);

  const {
    data: fetchedLinks,
    isLoading,
    refetch,
  } = useFolderShareLinks(resourceType, id, folderId);

  useEffect(() => {
    setLinks(fetchedLinks || []);
  }, [fetchedLinks]);

  const buildUrl = (token: string, targetFolderId: string) =>
    `${window.location.origin}/shared/folder/${targetFolderId}?token=${token}`;

  const createLink = async () => {
    if (creating) return;
    try {
      setCreating(true);
      const result = await (isEstate
        ? createEstateFolderShare({ estateId: id, folderId, expiresIn, parts: [] })
        : shareProjectFolder({ folderId, expiresIn, parts: [] }));

      const url = buildUrl(result.token, result.folderId ?? folderId);
      await navigator.clipboard.writeText(url);
      toast.success(t('copied-url'));
      setIsDropdownOpen(false);
      await refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      toast.error(message || t('somethingWentWrong'));
    } finally {
      setCreating(false);
      setExpiresIn(730);
    }
  };

  const handleRevoke = async (token: string) => {
    try {
      if (isEstate) {
        await revokeEstateLink({ folderId, token });
      } else {
        await revokeProjectLink({ folderId, token });
      }
      await refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      toast.error(message || t('somethingWentWrong'));
    }
  };

  const activeLinks = useMemo(() => links.filter((l) => dayjs().isBefore(l.expiryDate)), [links]);

  return (
    <ModalBase className="w-[40vw]">
      <X className="ml-auto cursor-pointer stroke-[4px] text-primary-light" onClick={closeModal} />
      <section className="mt-4 flex flex-row justify-between">
        <h3 className="text-xl font-semibold">{t('shared-link')}</h3>
        <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button disabled={isLoading}>{t('createNewLink')}</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="px-3 py-4">
            <div className="flex flex-col gap-3">
              <Input
                label={t('expires-in')}
                value={expiresIn}
                type="number"
                onChange={(e) => setExpiresIn(Number(e.target.value) || 0)}
              />
              <div className="flex gap-2">
                <Button
                  onClick={() => setIsDropdownOpen(false)}
                  variant="outline"
                  className="border-red-500 text-red-500"
                >
                  {t('cancel')}
                </Button>
                <Button onClick={createLink} disabled={creating}>
                  {t('share')}
                </Button>
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </section>
      {activeLinks.length > 0 ? (
        <ShareArchiveLinksTable
          links={activeLinks}
          fallbackFolderId={folderId}
          onRevoke={handleRevoke}
        />
      ) : (
        <div className="flex justify-center py-16">
          <span>{t('noDocuments')}</span>
        </div>
      )}
    </ModalBase>
  );
};
